<div class="flex flex-col gap-6">
    <!-- Selected evaluation -->
    @if (evaluations?.length > 1) {
        <div
            class="overflow-hidden rounded-xl border border-slate-200 bg-white"
        >
            <div
                class="relative flex items-center border-b border-slate-100 px-5 py-4"
            >
                <div
                    class="absolute bottom-0 top-0 w-1 bg-primary-500 ltr:left-0 rtl:right-0"
                ></div>
                <label
                    class="font-semibold text-slate-800 ltr:pl-3 rtl:pr-3"
                    for="evaluationSelect"
                >
                    {{ 'translate_select_the_evaluation' | translate }}
                </label>
            </div>
            <div class="p-5">
                <ng-select
                    id="evaluationSelect"
                    [items]="evaluations"
                    [ngModel]="selectedEvaluation"
                    [clearable]="false"
                    [searchable]="false"
                    (ngModelChange)="updateSelectedEvaluation($event)"
                    bindLabel="name"
                    class="w-full max-w-md"
                ></ng-select>
            </div>
        </div>
    }

    <!-- Overall Statistics -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div
            class="relative flex items-center border-b border-slate-100 px-5 py-4"
        >
            <div
                class="absolute bottom-0 top-0 w-1 bg-primary-500 ltr:left-0 rtl:right-0"
            ></div>
            <h3 class="font-semibold text-slate-800 ltr:pl-3 rtl:pr-3">
                {{ 'translate_overall_statistics' | translate }}
            </h3>
        </div>
        <div class="p-5">
            <app-content-loading [isLoading]="!overallStatistics">
                @if (overallStatistics) {
                    <div
                        [appCardStagger]="overallStatistics"
                        class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
                    >
                        <!-- Overall count -->
                        <div
                            animate.enter="flipping-card-enter"
                            class="group rounded-xl bg-blue-600 p-5 text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        >
                            <div class="flex items-center justify-between">
                                <p class="text-3xl font-bold">
                                    {{ overallStatistics?.count }}
                                </p>
                                <i
                                    class="fa-light fa-chart-bar text-2xl text-blue-300"
                                ></i>
                            </div>
                            <p class="mt-2 text-sm text-blue-100">
                                {{ 'translate_overall_count' | translate }}
                            </p>
                        </div>
                        <!-- Evaluated count -->
                        <div
                            animate.enter="flipping-card-enter"
                            class="group rounded-xl bg-emerald-600 p-5 text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        >
                            <div class="flex items-center justify-between">
                                <p class="text-3xl font-bold">
                                    {{ overallStatistics?.evaluatedCount }}
                                </p>
                                <i
                                    class="fa-light fa-check-circle text-2xl text-emerald-300"
                                ></i>
                            </div>
                            <p class="mt-2 text-sm text-emerald-100">
                                {{ 'translate_evaluated_count' | translate }}
                            </p>
                        </div>
                        <!-- Not evaluated count -->
                        <div
                            animate.enter="flipping-card-enter"
                            class="group rounded-xl bg-rose-600 p-5 text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        >
                            <div class="flex items-center justify-between">
                                <p class="text-3xl font-bold">
                                    {{ overallStatistics?.notEvaluatedCount }}
                                </p>
                                <i
                                    class="fa-light fa-clock text-2xl text-rose-300"
                                ></i>
                            </div>
                            <p class="mt-2 text-sm text-rose-100">
                                {{
                                    'translate_not_evaluated_count' | translate
                                }}
                            </p>
                        </div>
                        <!-- Average -->
                        <div
                            animate.enter="flipping-card-enter"
                            class="group rounded-xl bg-amber-500 p-5 text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        >
                            <div class="flex items-center justify-between">
                                <p class="text-3xl font-bold">
                                    {{
                                        $safeNavigationMigration(
                                            overallStatistics?.average
                                        ) !== null
                                            ? ($safeNavigationMigration(
                                                  overallStatistics?.average
                                              ) * 100 | round) + '%'
                                            : 'N/A'
                                    }}
                                </p>
                                <i
                                    class="fa-light fa-gauge-high text-2xl text-amber-200"
                                ></i>
                            </div>
                            <p class="mt-2 text-sm text-amber-100">
                                {{ 'translate_average_evaluation' | translate }}
                            </p>
                        </div>
                    </div>
                }
            </app-content-loading>
        </div>
    </div>

    <!-- Score Bands Statistics -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div
            class="relative flex items-center border-b border-slate-100 px-5 py-4"
        >
            <div
                class="absolute bottom-0 top-0 w-1 bg-primary-500 ltr:left-0 rtl:right-0"
            ></div>
            <h3 class="font-semibold text-slate-800 ltr:pl-3 rtl:pr-3">
                {{ 'translate_score_bands_statistics' | translate }}
            </h3>
        </div>
        <div class="p-5">
            <app-list-loading [items]="scoreBandStatistics">
                <div
                    [appCardStagger]="scoreBandStatistics?.length"
                    class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                >
                    @for (item of scoreBandStatistics; track item) {
                        <div
                            animate.enter="vertical-flipping-card-enter"
                            class="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:shadow-md"
                        >
                            <!-- Color header bar -->
                            <div
                                class="h-2"
                                [style.background-color]="item.color"
                            ></div>
                            <!-- Content -->
                            <div class="p-5">
                                <!-- Band name with color dot -->
                                <div class="mb-4 flex items-center gap-2">
                                    <span
                                        class="h-3 w-3 rounded-full"
                                        [style.background-color]="item.color"
                                    ></span>
                                    <h4 class="font-semibold text-slate-800">
                                        {{ item.name }}
                                    </h4>
                                </div>
                                <!-- Stats grid -->
                                <div class="grid grid-cols-2 gap-4">
                                    <!-- Count -->
                                    <div>
                                        <p class="text-sm text-slate-500">
                                            {{ 'translate_count' | translate }}
                                        </p>
                                        <p
                                            class="text-2xl font-bold text-slate-800"
                                        >
                                            {{ item.count }}
                                        </p>
                                    </div>
                                    <!-- Average -->
                                    <div>
                                        <p class="text-sm text-slate-500">
                                            {{
                                                'translate_average' | translate
                                            }}
                                        </p>
                                        <p
                                            class="text-2xl font-bold"
                                            [style.color]="item.color"
                                        >
                                            {{
                                                item.average !== null
                                                    ? (item.average * 100
                                                          | round) + '%'
                                                    : 'N/A'
                                            }}
                                        </p>
                                    </div>
                                </div>
                                <!-- Progress bar -->
                                <div class="mt-4">
                                    <div
                                        class="h-2 overflow-hidden rounded-full bg-slate-100"
                                    >
                                        <div
                                            class="h-full rounded-full transition-all"
                                            [style.background-color]="
                                                item.color
                                            "
                                            [style.width.%]="
                                                item.average !== null
                                                    ? item.average * 100
                                                    : 0
                                            "
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </app-list-loading>
        </div>
    </div>
</div>
